import {
  ALWAYS_VIEW_PAGE_KEYS,
  canAccessItsmHub,
  getPageKeyByPath as defaultGetPageKeyByPath,
} from "@repo/permissions";
import { DEFINITION_HUB_IDS, HEADER_TAB_HUB_IDS, pruneDefinitionHubNavItems } from "./sidebar-nav.hubs.js";
import { navBranchContainsPath } from "./sidebar-nav.utils.js";

const ALWAYS_VIEW_PAGE_KEY_SET = new Set(ALWAYS_VIEW_PAGE_KEYS);

/**
 * Apply the same role + license visibility rules used by the app sidebar.
 * @param {{ sections?: unknown[], navMain: Array<Record<string, unknown>> }} navData
 * @param {{ canView: (pageKey: string) => boolean, can: (pageKey: string, action: string) => boolean, partyAssignment?: Record<string, boolean>, featureFlags?: Record<string, boolean>, getPageKeyByPath?: (path: string) => string|null|undefined }} access
 */
export function filterSidebarNavByAccess(navData, access) {
  const canView = access.canView;
  const can = access.can;
  const partyAssignment = access.partyAssignment || {};
  const featureFlags = access.featureFlags;
  const getPageKeyByPath = access.getPageKeyByPath || defaultGetPageKeyByPath;

  const cloneNavItems = (items) =>
    (items || [])
      .filter((item) => !item.featureKey || !featureFlags || featureFlags[item.featureKey] === true)
      .map((item) => ({
        ...item,
        items: item.items ? cloneNavItems(item.items) : undefined,
      }));

  const cloned = {
    ...navData,
    navMain: (navData.navMain || []).map((item) => ({
      ...item,
      items: item.items ? cloneNavItems(item.items) : undefined,
    })),
  };

  const definitionsNav = cloned.navMain.find((item) => item.id === "definitions");

  if (definitionsNav && Array.isArray(definitionsNav.items)) {
    definitionsNav.items = definitionsNav.items
      .map((sub) => {
        if (!DEFINITION_HUB_IDS.has(sub.id)) return sub;
        const children = pruneDefinitionHubNavItems(sub.items, canView, getPageKeyByPath);
        if (!children.length) return null;
        return { ...sub, url: "#", items: children };
      })
      .filter(Boolean);

    if (!canView("definitionsReportDesign")) {
      definitionsNav.items = definitionsNav.items.filter((sub) => sub.id !== "report-branding");
    }
  }

  if (definitionsNav && Array.isArray(definitionsNav.items) && definitionsNav.items.length === 0) {
    cloned.navMain = cloned.navMain.filter((item) => item !== definitionsNav);
  }

  const canViewVendorRegistry = canView("vendorManagementVendors");
  const canViewVendorAssessmentsNav = canView("vendorManagementAssessments");
  const vendorMgmtNav = cloned.navMain.find((item) => item.id === "vendor-management");
  if (vendorMgmtNav && Array.isArray(vendorMgmtNav.items)) {
    vendorMgmtNav.items = vendorMgmtNav.items.filter((sub) => {
      if (sub.url === "/vendor-management/vendors") return canViewVendorRegistry;
      if (sub.url === "/vendor-management/vendor-assessments") return canViewVendorAssessmentsNav;
      return true;
    });
  }
  if (vendorMgmtNav && Array.isArray(vendorMgmtNav.items) && vendorMgmtNav.items.length === 0) {
    cloned.navMain = cloned.navMain.filter((item) => item.id !== "vendor-management");
  }

  const assetInventory = cloned.navMain.find((item) => item.id === "asset-inventory");

  if (assetInventory && Array.isArray(assetInventory.items)) {
    const canViewAssetLeaf = (sub) => {
      if (sub.url === "/asset-inventory-management/asset-group") return canView("assetInventoryAssetGroup");
      if (sub.url === "/asset-inventory-management/asset-category") return canView("assetInventoryAssetCategory");
      if (sub.url === "/asset-inventory-management/assets") return canView("assetInventoryAssets");
      if (sub.url === "/asset-inventory-management/license-management") return canView("assetInventoryLicenseManagement");
      if (sub.url === "/asset-inventory-management/maintenance-management") return canView("assetInventoryMaintenance");
      if (sub.url === "/asset-inventory-management/disposal-management") return canView("assetInventoryDisposalManagement");
      if (sub.url === "/asset-inventory-management/asset-lifecycle-events") return canView("assetInventoryLifecycleEvents");
      if (sub.url === "/asset-inventory-management/pd-inventory") return canView("assetInventoryPdInventory");
      return true;
    };

    const filterAssetNavItems = (items) =>
      (items || [])
        .map((sub) => {
          if (Array.isArray(sub.items) && sub.items.length > 0) {
            const children = filterAssetNavItems(sub.items);
            if (children.length === 0) return null;
            return { ...sub, items: children };
          }
          return canViewAssetLeaf(sub) ? sub : null;
        })
        .filter(Boolean);

    assetInventory.items = filterAssetNavItems(assetInventory.items);
  }

  // If Asset Inventory has no visible children left, hide the whole group
  if (assetInventory && Array.isArray(assetInventory.items) && assetInventory.items.length === 0) {
    cloned.navMain = cloned.navMain.filter((item) => item !== assetInventory);
  }

  const bigdesOperations = cloned.navMain.find((item) => item.id === "bigdes-operations");

  if (bigdesOperations && Array.isArray(bigdesOperations.items)) {
    bigdesOperations.items = bigdesOperations.items
      .map((sub) => {
        if (HEADER_TAB_HUB_IDS.has(sub.id)) {
          const children = pruneDefinitionHubNavItems(sub.items, canView, getPageKeyByPath);
          if (!children.length) return null;
          return { ...sub, url: "#", items: children };
        }
        if (sub.url === "/bigdes-operations/measure-applicability") {
          return canView("bigdesMeasureApplicability") ? sub : null;
        }
        if (sub.url === "/bigdes-operations/current-situation-analysis") {
          return canView("bigdesCurrentSituationAnalysis") ? sub : null;
        }
        if (sub.url === "/bigdes-operations/road-map") {
          return canView("bigdesRoadMap") ? sub : null;
        }
        if (sub.url === "/bigdes-operations/compensatory-control") {
          return canView("bigdesCompensatoryControl") ? sub : null;
        }
        return sub;
      })
      .filter(Boolean);

    if (bigdesOperations.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== bigdesOperations);
    }
  }

  const filterPageKeyedLeaves = (items) =>
    (items || []).filter((sub) => {
      if (Array.isArray(sub.anyPageKeys)) {
        const candidatePageKeys = [sub.pageKey, ...sub.anyPageKeys].filter(Boolean);
        return candidatePageKeys.some((pageKey) => canView(pageKey));
      }
      if (!sub.pageKey) return true;
      if (sub.partyEntityType && partyAssignment[sub.partyEntityType]) return true;
      return canView(sub.pageKey);
    });

  for (const groupId of ["documents-forms", "reports"]) {
    const group = cloned.navMain.find((item) => item.id === groupId && Array.isArray(item.items));
    if (!group) continue;
    group.items = filterPageKeyedLeaves(group.items);
    if (group.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== group);
    }
  }

  // Organizational context (4.1 / context management) — own nav group
  const organizationalContext = cloned.navMain.find(
    (item) => item.id === "organizational-context" && Array.isArray(item.items),
  );

  if (organizationalContext && Array.isArray(organizationalContext.items)) {
    if (!canView("isoContext")) {
      organizationalContext.items = organizationalContext.items.filter(
        (sub) => sub.url !== "/context-management/context",
      );
    }
    if (!canView("contextManagementInterestedParties")) {
      organizationalContext.items = organizationalContext.items.filter(
        (sub) => sub.url !== "/context-management/interested-parties",
      );
    }
    if (!canView("contextManagementScopeDocument")) {
      organizationalContext.items = organizationalContext.items.filter(
        (sub) => sub.url !== "/context-management/scope-document",
      );
    }
    if (!canView("isoCommunication")) {
      organizationalContext.items = organizationalContext.items.filter(
        (sub) => sub.url !== "/context-management/communication",
      );
    }
    if (!canView("isoMeeting")) {
      organizationalContext.items = organizationalContext.items.filter(
        (sub) => sub.url !== "/context-management/meeting",
      );
    }
    if (!canView("isoObjective")) {
      organizationalContext.items = organizationalContext.items.filter(
        (sub) => sub.url !== "/isms-controls/objective",
      );
    }
    if (organizationalContext.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== organizationalContext);
    }
  }

  // Training — list, my training, competence
  const trainingNav = cloned.navMain.find(
    (item) => item.id === "training" && Array.isArray(item.items),
  );

  if (trainingNav && Array.isArray(trainingNav.items)) {
    trainingNav.items = trainingNav.items.filter((sub) => {
      const pageKey = sub.pageKey || getPageKeyByPath(sub.url);
      return pageKey ? canView(pageKey) : false;
    });

    if (trainingNav.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== trainingNav);
    }
  }

  // Privacy — nested inventory / consent / subject groups; every leaf needs View.
  const filterPrivacyNavItems = (items) =>
    (items || [])
      .map((sub) => {
        if (Array.isArray(sub.items) && sub.items.length > 0) {
          const children = filterPrivacyNavItems(sub.items);
          if (children.length === 0) return null;
          return { ...sub, items: children };
        }
        const pageKey = sub.pageKey || (sub.url && sub.url !== "#" ? getPageKeyByPath(sub.url) : null);
        return pageKey && canView(pageKey) ? sub : null;
      })
      .filter(Boolean);

  const gdprOperations = cloned.navMain.find(
    (item) => item.id === "gdpr-operations" && Array.isArray(item.items),
  );

  if (gdprOperations && Array.isArray(gdprOperations.items)) {
    gdprOperations.items = filterPrivacyNavItems(gdprOperations.items);
    if (gdprOperations.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== gdprOperations);
    }
  }

  // Risk Analysis Operations group (match by stable id)
  const riskOperations = cloned.navMain.find(
    (item) => item.id === "risk-operations" && Array.isArray(item.items),
  );

  if (riskOperations && Array.isArray(riskOperations.items)) {
    // Risk Monitoring: hide if no View permission
    const canViewRiskMonitoring = canView("riskOperationsMonitoring");
    if (!canViewRiskMonitoring) {
      riskOperations.items = riskOperations.items.filter(
        (sub) => sub.url !== "/risk-management/risk-monitoring",
      );
    }

    const canViewRiskRegister = canView("riskOperationsRiskRegister");
    if (!canViewRiskRegister) {
      riskOperations.items = riskOperations.items.filter(
        (sub) => sub.url !== "/risk-management/risk-register",
      );
    }

    const canViewRiskTreatment = canView("riskOperationsTreatment");
    if (!canViewRiskTreatment) {
      riskOperations.items = riskOperations.items.filter(
        (sub) => sub.url !== "/risk-management/risk-treatment",
      );
    }

    const canViewResidual = canView("riskOperationsResidualRiskAcceptance");
    if (!canViewResidual) {
      riskOperations.items = riskOperations.items.filter(
        (sub) => sub.url !== "/risk-management/residual-risk-acceptance",
      );
    }

    const canViewOpportunities = canView("riskOperationsRiskOpportunities");
    if (!canViewOpportunities) {
      riskOperations.items = riskOperations.items.filter(
        (sub) => sub.url !== "/risk-management/risk-opportunities",
      );
    }

    if (riskOperations.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== riskOperations);
    }
  }

  const internalControl = cloned.navMain.find(
    (item) => item.id === "internal-control" && Array.isArray(item.items),
  );

  if (internalControl && Array.isArray(internalControl.items)) {
    const canViewControls = canView("internalControlControl");
    if (!canViewControls) {
      internalControl.items = internalControl.items.filter(
        (sub) => sub.url !== "/isms-controls/control",
      );
    }

    const canViewSoa = canView("ismsControlsSoa");
    if (!canViewSoa) {
      internalControl.items = internalControl.items.filter(
        (sub) => sub.url !== "/isms-controls/soa",
      );
    }

    if (internalControl.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== internalControl);
    }
  }

  const threatVulnerability = cloned.navMain.find(
    (item) => item.id === "threat-vulnerability" && Array.isArray(item.items),
  );

  if (threatVulnerability && Array.isArray(threatVulnerability.items)) {
    if (!canView("ismsControlsThreatIntelligence")) {
      threatVulnerability.items = threatVulnerability.items.filter(
        (sub) => sub.url !== "/isms-controls/threat-intelligence",
      );
    }
    if (!canView("ismsControlsVulnerability")) {
      threatVulnerability.items = threatVulnerability.items.filter(
        (sub) => sub.url !== "/isms-controls/vulnerability",
      );
    }
    if (threatVulnerability.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== threatVulnerability);
    }
  }

  const internalAudit = cloned.navMain.find(
    (item) => item.id === "internal-audit" && Array.isArray(item.items),
  );

  if (internalAudit && Array.isArray(internalAudit.items)) {
    const canViewAssessmentQuestions = canView("internalAuditAssessmentQuestions");
    if (!canViewAssessmentQuestions) {
      internalAudit.items = internalAudit.items.filter(
        (sub) => sub.url !== "/isms-controls/assessment-questions",
      );
    }

    const canViewAuditCalendar = canView("internalAuditAuditCalendar");
    if (!canViewAuditCalendar) {
      internalAudit.items = internalAudit.items.filter(
        (sub) => sub.url !== "/audit-compliance/audit-program",
      );
    }

    const canViewMyAudits = canView("internalAuditMyAudits");
    if (!canViewMyAudits) {
      internalAudit.items = internalAudit.items.filter(
        (sub) => sub.url !== "/audit-compliance/my-audits",
      );
    }

    const canViewAssessment = canView("internalAuditAssessment");
    if (!canViewAssessment) {
      internalAudit.items = internalAudit.items.filter(
        (sub) => sub.url !== "/audit-compliance/audit-assessment",
      );
    }
    const canViewCapa = canView("auditComplianceCapa");
    if (!canViewCapa) {
      internalAudit.items = internalAudit.items.filter(
        (sub) => sub.url !== "/audit-compliance/capa",
      );
    }

    const canViewKpiDashboard = canView("auditComplianceKpiDashboard");
    if (!canViewKpiDashboard) {
      internalAudit.items = internalAudit.items.filter(
        (sub) => sub.url !== "/audit-compliance/kpi-dashboard",
      );
    }

    if (internalAudit.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== internalAudit);
    }
  }

  // Incident Management group
  const incidentManagement = cloned.navMain.find(
    (item) =>
      item.url === "#" &&
      Array.isArray(item.items) &&
      item.items.some(
        (sub) =>
          sub.url === "/incident-management/incidents",
      ),
  );

  if (incidentManagement && Array.isArray(incidentManagement.items)) {

    const canViewMonitoring = canView("incidentManagementMonitoring");
    if (!canViewMonitoring) {
      incidentManagement.items = incidentManagement.items.filter(
        (sub) => sub.url !== "/incident-management/incidents/monitoring",
      );
    }

    const canViewIncidents = canView("incidentManagementIncidents");
    if (!canViewIncidents) {
      incidentManagement.items = incidentManagement.items.filter(
        (sub) => sub.url !== "/incident-management/incidents"
          && sub.url !== "/incident-management/templates",
      );
    }

    const canViewActions = canView("incidentActions");
    if (!canViewActions) {
      incidentManagement.items = incidentManagement.items.filter(
        (sub) => sub.url !== "/incident-management/actions",
      );
    }

    if (incidentManagement.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== incidentManagement);
    }
  }

  // Platform management (templates, comms, integrations, site ops)
  const platformManagement = cloned.navMain.find(
    (item) => item.helperKey === "platformManagement",
  );
  if (platformManagement && Array.isArray(platformManagement.items)) {
    const canViewPlatformLeaf = (sub) => {
      if (sub.url === "/admin/queues") {
        return canView("platformQueueMonitor") || canView("userOperationsQueueMonitor");
      }
      if (sub.url === "/platform/party-templates") {
        return canView("platformPartyTemplates");
      }
      if (sub.url === "/platform/assignment-templates") {
        return canView("platformAssignmentTemplates");
      }
      if (sub.url === "/platform/workflow-templates") {
        return canView("platformWorkflowTemplates");
      }
      if (sub.url === "/platform/business-calendar") {
        return canView("platformBusinessCalendar");
      }
      if (sub.url === "/platform/tailorability" || sub.url === "/platform/seed-policy") {
        return canView("platformTailorability");
      }
      if (sub.url === "/admin/pending-workflow-operations") {
        return canView("adminPendingWorkflowOperations");
      }
      if (sub.url === "/admin/dead-letter-queue") {
        return canView("adminDeadLetterQueue");
      }
      if (sub.url === "/admin/webhooks") {
        return canView("adminWebhooks");
      }
      if (sub.url === "/admin/backups") {
        return canView("adminBackup");
      }
      if (sub.url === "/platform/third-party-applications") {
        return canView("platformThirdPartyApplications");
      }
      if (sub.url === "/platform/communications/channels") {
        return canView("communicationProvider");
      }
      if (sub.url === "/platform/communications/mfa") {
        return canView("platformMfaSettings");
      }
      if (sub.url === "/platform/communications/templates") {
        return canView("definitionsGeneralSystemMailTemplate");
      }
      if (sub.url === "/platform/communications/notification-policies") {
        return canView("platformNotificationPolicies");
      }
      if (sub.url === "/platform/communications/delivery-history") {
        return can("platformNotificationPolicies", "ViewDispatches");
      }
      if (sub.url === "/admin/compliance-frameworks") {
        return canView("adminTenantCompliance");
      }
      if (sub.url === "/admin/data-export-import") {
        return canView("adminDataExportImport");
      }
      if (sub.url === "/admin/tenant-onboarding") {
        return canView("adminTenantOnboarding");
      }
      if (sub.url === "/user-operations/system-logs") {
        return canView("userOperationsSystemLogs");
      }
      return true;
    };

    const filterPlatformNavItems = (items) =>
      (items || [])
        .map((sub) => {
          if (Array.isArray(sub.items) && sub.items.length > 0) {
            const children = filterPlatformNavItems(sub.items);
            if (children.length === 0) return null;
            return { ...sub, items: children };
          }
          return canViewPlatformLeaf(sub) ? sub : null;
        })
        .filter(Boolean);

    platformManagement.items = filterPlatformNavItems(platformManagement.items);
    if (platformManagement.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== platformManagement);
    }
  }

  // User Operations group
  const userOperations = cloned.navMain.find(
    (item) =>
      item.url === "#" &&
      Array.isArray(item.items) &&
      item.items.some((sub) => sub.url === "/user-operations/user-management"),
  );

  if (userOperations && Array.isArray(userOperations.items)) {
    const canViewUserList = canView("userOperationsUserManagement");
    if (!canViewUserList) {
      userOperations.items = userOperations.items.filter(
        (sub) => sub.url !== "/user-operations/user-management",
      );
    }

    const canViewRole = canView("userOperationsRoleManagement");
    if (!canViewRole) {
      userOperations.items = userOperations.items.filter(
        (sub) => sub.url !== "/user-operations/role-management",
      );
    }

    const canViewSystemLogs = canView("userOperationsSystemLogs");
    if (!canViewSystemLogs) {
      userOperations.items = userOperations.items.filter(
        (sub) => sub.url !== "/user-operations/system-logs",
      );
    }

    const canViewDelegations = canView("userOperationsDelegations");
    if (!canViewDelegations) {
      userOperations.items = userOperations.items.filter(
        (sub) => sub.url !== "/user-operations/delegations",
      );
    }

    const canViewAdminDelegations = canView("adminDelegations");
    if (!canViewAdminDelegations) {
      userOperations.items = userOperations.items.filter(
        (sub) => sub.url !== "/admin/delegations",
      );
    }

    if (userOperations.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== userOperations);
    }
  }

  // My Work / Home / Dashboard: always visible (ALWAYS_VIEW_PAGE_KEYS).
  cloned.navMain = cloned.navMain.filter((item) => {
    if (item.section === "quickAccess" && item.pageKey && ALWAYS_VIEW_PAGE_KEY_SET.has(item.pageKey)) {
      return true;
    }
    if (item.url === "/my-work") return true;
    if (item.section === "quickAccess" && item.pageKey) return canView(item.pageKey);
    return true;
  });


  // Find BCM group (Business Continuity Management)
  const bcm = cloned.navMain.find(
    (item) =>
      item.url === "#" &&
      Array.isArray(item.items) &&
      item.items.some((sub) => sub.url === "/bcm/bia"),
  );

  if (bcm && Array.isArray(bcm.items)) {
    // BIA: hide if no View permission
    if (!canView("bcmBia")) {
      bcm.items = bcm.items.filter((sub) => sub.url !== "/bcm/bia");
    }

    // BCP: hide if no View permission
    if (!canView("bcmBcp")) {
      bcm.items = bcm.items.filter((sub) => sub.url !== "/bcm/bcp");
    }

    // Crisis Event: hide if no View permission
    if (!canView("bcmCrisisEvent")) {
      bcm.items = bcm.items.filter((sub) => sub.url !== "/bcm/crisis-event");
    }

    // Exercises: hide if no View permission
    if (!canView("bcmExercises")) {
      bcm.items = bcm.items.filter((sub) => sub.url !== "/bcm/exercises");
    }

    // If BCM has no visible children left, hide the whole group
    if (bcm.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== bcm);
    }
  }

  // Ticketing: submenu items use distinct page keys in the registry; hide without View
  const ticketing = cloned.navMain.find(
    (item) =>
      item.url === "#" &&
      Array.isArray(item.items) &&
      item.items.some((sub) => sub.url === "/ticketing" || sub.url === "/ticketing/dashboard"),
  );

  if (ticketing && Array.isArray(ticketing.items)) {
    const canTicketOps = canView("ticketing");

    ticketing.items = ticketing.items.filter((sub) => {
      if (sub.url === "/ticketing/dashboard" || sub.url === "/ticketing" || sub.url === "/ticketing/my-tasks") {
        return canTicketOps;
      }
      if (sub.url === "/ticketing/templates") {
        return canView("ticketTemplate");
      }
      if (sub.url === "/ticketing/settings") {
        return canView("ticketConfig");
      }
      return true;
    });

    if (ticketing.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== ticketing);
    }
  }

  const itsm = cloned.navMain.find(
    (item) =>
      item.url === "#" &&
      Array.isArray(item.items) &&
      item.items.some((sub) => sub.url === "/itsm/service-catalog"),
  );

  if (itsm && Array.isArray(itsm.items)) {
    if (!canAccessItsmHub(canView)) {
      itsm.items = itsm.items.filter((sub) => sub.url !== "/itsm/hub");
    }
    if (!canView("itsmServiceCatalog")) {
      itsm.items = itsm.items.filter((sub) => sub.url !== "/itsm/service-catalog");
    }
    if (!canView("itsmChangeRequest")) {
      itsm.items = itsm.items.filter((sub) => sub.url !== "/itsm/change-request");
    }
    if (!canView("itsmProblem")) {
      itsm.items = itsm.items.filter((sub) => sub.url !== "/itsm/problem");
    }
    if (!canView("itsmSupplierContracts")) {
      itsm.items = itsm.items.filter((sub) => sub.url !== "/itsm/supplier-contracts");
    }
    itsm.items = itsm.items.filter((sub) => sub.url !== "/itsm/sla-management");
    if (itsm.items.length === 0) {
      cloned.navMain = cloned.navMain.filter((item) => item !== itsm);
    }
  }

  return cloned;
}

/**
 * @param {{ navMain?: Array<{ id?: string, url?: string, items?: Array<{ id?: string, url?: string }> }> }} filteredNav
 * @returns {Set<string>}
 */
function walkNavItems(items, visit) {
  for (const item of items || []) {
    visit(item);
    if (Array.isArray(item?.items)) walkNavItems(item.items, visit);
  }
}

export function collectVisibleSidebarIds(filteredNav) {
  const ids = new Set();
  walkNavItems(filteredNav?.navMain, (item) => {
    if (item?.id) ids.add(item.id);
  });
  return ids;
}

/**
 * @param {{ navMain?: Array<{ url?: string, items?: Array<{ url?: string }> }> }} filteredNav
 * @returns {string[]}
 */
export function collectVisibleSidebarUrls(filteredNav) {
  const urls = [];
  walkNavItems(filteredNav?.navMain, (item) => {
    if (item?.url && item.url !== "#") urls.push(item.url);
  });
  return urls;
}

function normalizePath(path) {
  if (!path) return "";
  return String(path).split("?")[0].split("#")[0].replace(/\/$/, "") || "/";
}

/**
 * Whether a route is reachable via currently visible sidebar entries.
 * Uses the same path-matching rules as the sidebar (exact / exclude prefixes).
 * @param {string} path
 * @param {{ navMain?: Array<Record<string, unknown>> }} filteredNav
 */
export function isPathVisibleInSidebar(path, filteredNav) {
  const clean = normalizePath(path);
  if (!clean) return false;
  const navMain = filteredNav?.navMain || [];
  if (navMain.some((item) => navBranchContainsPath(clean, item))) return true;
  // Hub parents that are not leaves (url "#") but still have visible children under the path
  const urls = collectVisibleSidebarUrls(filteredNav).map(normalizePath);
  return urls.some((url) => url.startsWith(`${clean}/`));
}

/** About-sgrc module/submodule id → sidebar nav id */
export const ABOUT_SGRC_TO_SIDEBAR_ID = {
  home: "home",
  "my-work": "my-work",
  setup: "setup-wizard",
  definitions: "definitions",
  "def-org": "organization",
  "def-scale": "scale",
  "def-kvkk": "personal-data",
  "def-compliance": "compliance-policy",
  "def-class": "document",
  "def-platform": "platform",
  "vendor-management": "vendor-management",
  "asset-inventory": "asset-inventory",
  "bigdes-operations": "bigdes-operations",
  reports: "reports",
  context: "organizational-context",
  isms: "organizational-context",
  bcm: "bcm",
  ticketing: "ticketing",
  itsm: "itsm",
  gdpr: "gdpr-operations",
  risk: "risk-operations",
  "internal-control": "internal-control",
  "internal-audit": "internal-audit",
  "incident-management": "incident-management",
  "platform-management": "platform-management",
};

/** Meta guides with no sidebar counterpart — always visible */
export const ABOUT_SGRC_ALWAYS_VISIBLE_IDS = new Set(["sidebar"]);

/**
 * Filter About-sgrc module tree by visible sidebar nav ids.
 * @param {Array<{ id: string, items?: Array<{ id: string }> }>} modulesList
 * @param {Set<string>} visibleSidebarIds
 */
export function filterAboutSgrcModulesBySidebarIds(modulesList, visibleSidebarIds) {
  return (modulesList || [])
    .map((mod) => {
      if (ABOUT_SGRC_ALWAYS_VISIBLE_IDS.has(mod.id)) return mod;
      const sidebarId = ABOUT_SGRC_TO_SIDEBAR_ID[mod.id];
      if (!sidebarId) return mod;
      if (!visibleSidebarIds.has(sidebarId)) return null;
      if (!Array.isArray(mod.items) || mod.items.length === 0) return mod;
      const items = mod.items.filter((sub) => {
        if (ABOUT_SGRC_ALWAYS_VISIBLE_IDS.has(sub.id)) return true;
        const subSidebarId = ABOUT_SGRC_TO_SIDEBAR_ID[sub.id];
        if (!subSidebarId) return true;
        return visibleSidebarIds.has(subSidebarId);
      });
      if (items.length === 0) return null;
      return { ...mod, items };
    })
    .filter(Boolean);
}
